import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Stack,
  Typography,
  css,
} from "@mui/material";
import { useState, useMemo, useCallback, type ReactNode } from "react";
import { v4 as uuid } from "uuid";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/Download";
import {
  DataGridPro,
  GridColDef,
  GridRowClassNameParams,
  GridActionsCellItem,
} from "@mui/x-data-grid-pro";
import { AddIcon } from "@/assets/icons";
import { CSVImportDialog } from "./CSVImportDialog";
import { SchemaDefinition, DuplicateRecordCriteria, FieldDefinition } from "./types";
import { Icon } from "@/components/Icon";

type OptionsManagerProps<T extends { id: string }> = {
  records: T[];
  onChange: (records: T[]) => void;
  schemaDefinition: SchemaDefinition<T>;
  duplicateRecordCriteria: DuplicateRecordCriteria;
  title: string;
  emptyMessage?: string;
  defaultExpanded?: boolean;
  useAccordion?: boolean;
  onEdit?: (record: T) => void;
  onDelete?: (record: T) => void;
  isLoading?: boolean;
  toolbarExtra?: ReactNode;
};

const tableStyle = css`
  --border: 1px solid #e5e5e5;
  --border-radius: 1rem;

  border: none;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  .MuiDataGrid-root {
    width: 100%;
    max-width: 100%;
  }

  .MuiDataGrid-main {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .MuiDataGrid-virtualScroller {
    border-left: var(--border);
    border-right: var(--border);
    border-top: var(--border);
    border-top-left-radius: var(--border-radius);
    border-top-right-radius: var(--border-radius);
    box-sizing: border-box;
  }

  .MuiDataGrid-columnHeaders {
    border: none;

    .MuiDataGrid-columnSeparator svg {
      opacity: 0;
    }
  }

  .MuiDataGrid-footerContainer {
    border: var(--border);
    border-bottom-left-radius: var(--border-radius);
    border-bottom-right-radius: var(--border-radius);
    box-sizing: border-box;

    /* Fix pagination select dropdown spacing */
    .MuiTablePagination-select {
      padding-right: 24px !important;
    }

    .MuiSelect-icon {
      right: 0 !important;
    }
  }

  .record-row--even {
    background-color: #fff;
  }

  .record-row--odd {
    background-color: #f0f3ff;
  }
`;

const getRowClass = (params: GridRowClassNameParams) => {
  const isRowEven = params.indexRelativeToCurrentPage % 2 === 0;
  return `record-row--${isRowEven ? "even" : "odd"}`;
};

const normalizeValue = (value: any): string => {
  if (typeof value === "string") {
    return value.toUpperCase().trim();
  }
  return String(value).toUpperCase().trim();
};

const checkDuplicate = <T extends Record<string, any>>(
  record: T,
  existingRecord: T,
  criteria: DuplicateRecordCriteria
): boolean => {
  if (criteria.logic === "AND") {
    // All fields must match
    return criteria.fields.every((field) => {
      const recordValue = normalizeValue(record[field]);
      const existingValue = normalizeValue(existingRecord[field]);
      return recordValue === existingValue;
    });
  } else {
    // Any field matches
    return criteria.fields.some((field) => {
      const recordValue = normalizeValue(record[field]);
      const existingValue = normalizeValue(existingRecord[field]);
      return recordValue === existingValue;
    });
  }
};

export const OptionsManager = <T extends { id: string }>({
  records,
  onChange,
  schemaDefinition,
  duplicateRecordCriteria,
  title,
  emptyMessage = "No records configured. Click \"Add\" or \"Import CSV\" to get started.",
  defaultExpanded = true,
  useAccordion = true,
  onEdit,
  onDelete,
  isLoading = false,
  toolbarExtra,
}: OptionsManagerProps<T>) => {
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);

  const addRecord = useCallback(() => {
    const newRecord = {
      id: uuid(),
      ...schemaDefinition.defaultValues,
    } as T;
    onChange([...records, newRecord]);
  }, [records, onChange, schemaDefinition.defaultValues]);

  const handleEdit = useCallback(
    (record: T) => {
      if (onEdit) {
        onEdit(record);
      }
    },
    [onEdit]
  );

  const handleDelete = useCallback(
    (record: T) => {
      if (onDelete) {
        onDelete(record);
      } else {
        // Default delete behavior
        onChange(records.filter((r) => r.id !== record.id));
      }
    },
    [onDelete, onChange, records]
  );

  const handleCSVImport = useCallback(
    (importedRecords: (Omit<T, "id"> & { _existingId?: string })[]) => {
      // Separate updates and inserts
      const updatedRecords = [...records];
      const newRecords: T[] = [];

      importedRecords.forEach((imported) => {
        // Remove _existingId from the record
        const { _existingId, ...recordData } = imported;

        if (_existingId) {
          // Update existing record
          const existingIndex = updatedRecords.findIndex((r) => r.id === _existingId);
          if (existingIndex >= 0) {
            updatedRecords[existingIndex] = {
              ...updatedRecords[existingIndex],
              ...recordData,
            } as T;
          }
        } else {
          // Insert new record
          newRecords.push({
            ...recordData,
            id: uuid(),
          } as T);
        }
      });

      onChange([...updatedRecords, ...newRecords]);
    },
    [records, onChange]
  );

  const handleCSVExport = useCallback(() => {
    const csvContent = records
      .map((record) =>
        schemaDefinition.csvColumnOrder
          .map((fieldName) => {
            const value = record[fieldName as keyof T];
            return String(value ?? "");
          })
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${title.toLowerCase().replace(/\s+/g, "_")}_export.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [records, schemaDefinition.csvColumnOrder, title]);

  // Generate DataGrid columns from schema
  const columns = useMemo((): GridColDef[] => {
    const dataColumns: GridColDef[] = schemaDefinition.fields.map((field: FieldDefinition) => {
      const baseColumn: GridColDef = {
        field: field.name,
        headerName: field.label,
        width: field.width,
        flex: field.flex,
        editable: field.editable ?? false,
        ...(field.valueFormatter && {
          valueFormatter: (value) => {
            if (field.valueFormatter) {
              return field.valueFormatter(value);
            }
            return value;
          }
        }),
        ...field.gridColDefOverrides,
      };

      return baseColumn;
    });

    // Always add actions column with edit and delete buttons
    const actionsColumn: GridColDef = {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 120,
      getActions: (params) => [
        <GridActionsCellItem
          key="edit"
          icon={<Icon icon="edit" />}
          label="Edit"
          onClick={() => handleEdit(params.row as T)}
        />,
        <GridActionsCellItem
          key="delete"
          icon={<Icon icon="delete" />}
          label="Delete"
          onClick={() => handleDelete(params.row as T)}
          showInMenu
        />,
      ],
    };
    dataColumns.push(actionsColumn);

    return dataColumns;
  }, [schemaDefinition.fields, handleEdit, handleDelete]);

  const initState = { pagination: { paginationModel: { pageSize: 10, page: 0 } } };

  const content = (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end" }}>
        {toolbarExtra}
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleCSVExport}
          disabled={records.length === 0}
        >
          Export CSV
        </Button>
        <Button variant="outlined" onClick={() => setCsvDialogOpen(true)}>
          Import CSV
        </Button>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={addRecord}>
          Add {schemaDefinition.entityName || "Record"}
        </Button>
      </Stack>

      {records.length === 0 && !isLoading ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center", py: 4 }}
        >
          {emptyMessage}
        </Typography>
      ) : (
        <Stack style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
          <DataGridPro
            css={tableStyle}
            sx={{ width: "100%", maxWidth: "100%" }}
            initialState={initState}
            getRowClassName={getRowClass}
            loading={isLoading}
            rows={records}
            rowSelection={false}
            columns={columns}
            pagination
            pageSizeOptions={[10, 20, 40]}
            disableColumnMenu
          />
        </Stack>
      )}

      <CSVImportDialog
        open={csvDialogOpen}
        onClose={() => setCsvDialogOpen(false)}
        onImport={handleCSVImport}
        existingRecords={records}
        schemaDefinition={schemaDefinition}
        duplicateRecordCriteria={duplicateRecordCriteria}
        title={`Import ${schemaDefinition.entityNamePlural || title} from CSV`}
      />
    </Stack>
  );

  if (useAccordion) {
    return (
      <Accordion defaultExpanded={defaultExpanded}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h5">{title}</Typography>
        </AccordionSummary>
        <AccordionDetails>{content}</AccordionDetails>
      </Accordion>
    );
  }

  return content;
};
